import type { ReplyRecord as Record } from '@ithildin/shared';
import { MIN_SAMPLES_FOR_RECORD } from '@ithildin/shared';

function humanDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * A recipient's track record. Deliberately absent rather than optimistic when
 * there is too little history: showing "100% replied" from one message would
 * misrepresent a stranger to someone deciding whether to stake real money.
 */
export function ReplyRecord({
  record,
  windowHours,
}: {
  record: Record | null;
  windowHours: number;
}) {
  if (!record) {
    return (
      <div className="record record-empty">
        <span className="eyebrow">Track record</span>
        <p className="hint" style={{ margin: '8px 0 0' }}>
          Not enough answered messages yet to show a reliable figure — it appears after{' '}
          {MIN_SAMPLES_FOR_RECORD}. Their reply window is {windowHours}h, and you get your fee
          back if they miss it.
        </p>
      </div>
    );
  }

  const good = record.repliedPct >= 70;

  return (
    <div className="record">
      <span className="eyebrow">Track record</span>
      <div className="record-stats">
        <div className="stat">
          <span className={`stat-value${good ? ' is-good' : ''}`}>{record.repliedPct}%</span>
          <span className="stat-label">replied in time</span>
        </div>
        <div className="stat">
          <span className="stat-value">
            {record.medianReplyMinutes === null ? '—' : humanDuration(record.medianReplyMinutes)}
          </span>
          <span className="stat-label">typical reply</span>
        </div>
        <div className="stat">
          <span className="stat-value">{record.settled}</span>
          <span className="stat-label">paid messages</span>
        </div>
      </div>
    </div>
  );
}
